import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { ChaliceIcon, ShieldCrossIcon, CrosshairIcon, IchthysIcon } from "@/components/Icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const CAUSES = [
  { id: "General Fund", icon: ChaliceIcon, blurb: "Where the need is greatest — fellowship, facilities, and the daily work of the order." },
  { id: "Youth Safety Training", icon: ShieldCrossIcon, blurb: "Free firearm-safety instruction for the next generation of families." },
  { id: "Range Fund", icon: CrosshairIcon, blurb: "Targets, berms, and bay upkeep so every member trains on safe ground." },
  { id: "Benevolence Fund", icon: IchthysIcon, blurb: "Quiet aid for members and neighbors when hardship comes knocking." },
];

const TIERS = [
  { id: "friend", name: "Friend of the Order", amount: 25, note: "Covers a youth safety class for one student" },
  { id: "patron", name: "Patron", amount: 100, note: "Keeps a range bay supplied for a month" },
  { id: "benefactor", name: "Benefactor", amount: 500, note: "Sponsors a full day of instruction and fellowship" },
];

const inputCls = "mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";

export default function Donate() {
  const [cause, setCause] = useState(CAUSES[0].id);
  const [freq, setFreq] = useState("once");
  const [tier, setTier] = useState(TIERS[1].id);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedTier = TIERS.find((t) => t.id === tier);
  const isCustom = tier === "custom";
  const amount = isCustom ? parseFloat(custom) : selectedTier?.amount;

  const give = async () => {
    if (!amount || amount < 1) {
      toast.error("Enter a gift amount of at least $1");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount,
        cause,
        tier: isCustom ? "" : selectedTier.name,
        recurring: freq === "monthly",
        donor_name: name.trim(),
        origin_url: window.location.origin,
      });
      toast.success("Redirecting to secure checkout...");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="donate-page">
      <section className="relative overflow-hidden border-b border-gold/15 noise-overlay crosshair-grid">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-28 -left-28 w-96 h-96 rounded-full bg-crimson/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="eyebrow mb-3">Sponsorship &amp; Giving</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-parchment leading-tight">
              Fuel the <span className="text-gold">Mission</span>
            </h1>
            <p className="mt-5 text-stone-300 text-sm sm:text-base leading-relaxed max-w-2xl">
              Crusaders of the Cross runs on the generosity of friends who believe faith and responsible
              marksmanship belong together. Every gift trains a student, maintains a range, and steadies a family.
            </p>
            <p className="mt-5 font-serif italic text-lg text-stone-400 max-w-xl">
              &ldquo;Each of you should give what you have decided in your heart to give, not reluctantly or under
              compulsion, for God loves a cheerful giver.&rdquo; <span className="not-italic font-mono text-xs tracking-[0.3em] text-crimson uppercase">— 2 Cor 9:7</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-10">
            <Reveal>
              <div>
                <h2 className="font-display text-xl uppercase tracking-wider text-parchment mb-5">1 &mdash; Choose a Designation</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {CAUSES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`cause-${c.id.toLowerCase().replace(/ /g, "-")}`}
                      onClick={() => setCause(c.id)}
                      className={`text-left border p-5 transition-[border-color,background-color,box-shadow] duration-300 ${
                        cause === c.id
                          ? "border-gold bg-gold/10 shadow-[0_0_20px_rgba(212,175,55,0.15)]"
                          : "border-gold/20 bg-card2/40 hover:border-gold/50"
                      }`}
                    >
                      <c.icon className={`w-6 h-6 ${cause === c.id ? "text-gold" : "text-stone-500"}`} />
                      <p className="mt-3 font-condensed uppercase tracking-wider text-parchment">{c.id}</p>
                      <p className="mt-1.5 text-xs text-stone-400 leading-relaxed">{c.blurb}</p>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal>
              <div>
                <h2 className="font-display text-xl uppercase tracking-wider text-parchment mb-5">2 &mdash; Frequency</h2>
                <div className="grid grid-cols-2 border border-gold/25">
                  {[
                    { v: "once", label: "One-Time Gift" },
                    { v: "monthly", label: "Monthly Giving" },
                  ].map((f) => (
                    <button
                      key={f.v}
                      type="button"
                      data-testid={`freq-${f.v}`}
                      onClick={() => setFreq(f.v)}
                      className={`py-3 font-condensed uppercase tracking-[0.2em] text-sm transition-colors duration-300 ${
                        freq === f.v ? "bg-gold text-obsidian font-semibold" : "text-stone-400 hover:text-gold"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {freq === "monthly" && (
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold" data-testid="monthly-note">
                    Recurring — cancel anytime by contacting an officer
                  </p>
                )}
              </div>
            </Reveal>

            <Reveal>
              <div>
                <h2 className="font-display text-xl uppercase tracking-wider text-parchment mb-5">3 &mdash; Your Gift</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  {TIERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`tier-${t.id}`}
                      onClick={() => { setTier(t.id); setCustom(""); }}
                      className={`text-left border p-5 transition-[border-color,background-color,box-shadow] duration-300 ${
                        tier === t.id
                          ? "border-gold bg-gold/10 shadow-[0_0_20px_rgba(212,175,55,0.15)]"
                          : "border-gold/20 bg-card2/40 hover:border-gold/50"
                      }`}
                    >
                      <p className={`font-display text-3xl ${tier === t.id ? "text-gold" : "text-parchment"}`}>${t.amount}</p>
                      <p className="mt-1 font-condensed uppercase tracking-wider text-sm text-parchment">{t.name}</p>
                      <p className="mt-1.5 text-[11px] text-stone-500 leading-relaxed">{t.note}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-4 max-w-xs">
                  <Label className={labelCls}>Or a Custom Amount (USD)</Label>
                  <Input
                    data-testid="custom-amount-input"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Enter any amount"
                    value={custom}
                    onChange={(e) => { setCustom(e.target.value); if (e.target.value) setTier("custom"); }}
                    className={inputCls}
                  />
                </div>
                <div className="mt-4 max-w-xs">
                  <Label className={labelCls}>Your Name (optional)</Label>
                  <Input data-testid="donor-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="For our thank-you note" className={inputCls} />
                </div>
              </div>
            </Reveal>

            <Reveal>
              <button
                type="button"
                data-testid="donate-submit-button"
                onClick={give}
                disabled={busy}
                className="w-full sm:w-auto gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold px-12 py-4 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300 disabled:opacity-60"
              >
                {busy ? "One moment..." : `Give ${amount ? `$${amount}` : ""} ${freq === "monthly" ? "Monthly" : "Now"} — Securely`}
              </button>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Card processing by Stripe &bull; we never see your card number</p>
            </Reveal>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <Reveal delay={0.1}>
              <div className="border border-gold/25 bg-card2/50 p-7" data-testid="mailin-section">
                <h3 className="font-display text-lg uppercase tracking-wider text-parchment">Prefer to Give by Mail?</h3>
                <p className="mt-3 text-sm text-stone-300 leading-relaxed">
                  Send a check payable to <span className="text-gold">Crusaders of the Cross</span>, with your chosen
                  fund named in the memo line. Email <span className="text-gold">chaplain@crusadersofthecross.org</span> for
                  the current mailing address.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border border-gold/25 bg-card2/50 p-7">
                <h3 className="font-display text-lg uppercase tracking-wider text-parchment">Where Your Gift Goes</h3>
                <ul className="mt-4 space-y-3 text-sm text-stone-300">
                  <li className="flex gap-3"><ShieldCrossIcon className="w-5 h-5 text-gold shrink-0" /> Free safety courses for youth and first-time owners</li>
                  <li className="flex gap-3"><CrosshairIcon className="w-5 h-5 text-gold shrink-0" /> Range maintenance, targets, and training materials</li>
                  <li className="flex gap-3"><ChaliceIcon className="w-5 h-5 text-gold shrink-0" /> Chaplaincy, devotions, and fellowship meals</li>
                  <li className="flex gap-3"><IchthysIcon className="w-5 h-5 text-gold shrink-0" /> Benevolence for families facing hard seasons</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border border-crimson/30 bg-crimson/5 p-7">
                <p className="font-serif italic text-lg text-parchment leading-relaxed">
                  &ldquo;Whoever is generous to the poor lends to the Lord, and he will repay him for his deed.&rdquo;
                </p>
                <p className="mt-3 font-mono text-xs tracking-[0.3em] text-crimson uppercase">— Proverbs 19:17</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
